import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle, X } from "@phosphor-icons/react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import PlanToolbar from "../components/builder/PlanToolbar";
import WeekCard from "../components/builder/WeekCard";
import ExercisePicker from "../components/builder/ExercisePicker";
import ExerciseInfoModal from "../components/builder/ExerciseInfoModal";
import AddWeekMenu from "../components/builder/AddWeekMenu";
import { usePlanBuilder } from "../hooks/usePlanBuilder";
import { exportPlanToPDF } from "../lib/pdf";

export default function PlanBuilder() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tunedCount = parseInt(searchParams.get("tuned") || "0", 10);
  const [showTunedBanner, setShowTunedBanner] = useState(tunedCount > 0);
  const {
    plan, update, addWeek, removeWeek, updateWeekIntent, updateDay,
    addExercise, removeExercise, updateExercise, reorderExercises, replaceExercises,
    toggleComplete, toggleShare, progressWeek, save, assessment,
  } = usePlanBuilder(id);

  const [exercises, setExercises] = useState([]);
  const [exMap, setExMap] = useState({});
  const [picker, setPicker] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/exercises").then((r) => {
      setExercises(r.data);
      const m = {};
      r.data.forEach((e) => { m[e.id] = e; });
      setExMap(m);
    });
  }, []);

  const dismissBanner = () => {
    setShowTunedBanner(false);
    searchParams.delete("tuned");
    setSearchParams(searchParams, { replace: true });
  };

  if (!plan) return <Layout><div className="label-mono animate-pulse">LOADING...</div></Layout>;

  const has1RM = !!(assessment && (assessment.squat_1rm_lb || assessment.bench_1rm_lb || assessment.deadlift_1rm_lb || assessment.ohp_1rm_lb));

  const onPick = (exDef) => {
    addExercise(picker.wi, picker.di, exDef);
    setPicker(null);
  };

  const onShowExerciseInfo = (exerciseId, wi, di, exIdx) => setInfo({ exerciseId, wi, di, exIdx });

  const onSwapAlternate = (newId) => {
    if (info) updateExercise(info.wi, info.di, info.exIdx, { exercise_id: newId });
    setInfo(null);
  };

  return (
    <Layout>
      {showTunedBanner && (
        <div
          className="border border-volt bg-volt/10 p-4 mb-6 flex items-center gap-3"
          data-testid="tuned-banner"
        >
          <CheckCircle size={20} weight="bold" className="text-volt shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold uppercase tracking-wider text-sm">
              PLAN TUNED TO YOUR 1RMS
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Rescaled <span className="text-volt font-bold">{tunedCount}</span> main-lift weight{tunedCount === 1 ? "" : "s"} using your saved assessment numbers. Accessory and bodyweight prescriptions kept as-is.
            </div>
          </div>
          <button onClick={dismissBanner} data-testid="tuned-banner-dismiss" className="text-muted-foreground hover:text-foreground">
            <X size={18} weight="bold" />
          </button>
        </div>
      )}

      <PlanToolbar
        plan={plan}
        update={update}
        onExportPDF={() => exportPlanToPDF(plan, exMap)}
        onToggleShare={toggleShare}
        onSave={save}
      />

      {has1RM ? (
        <div
          className="border border-border bg-card/50 px-3 py-2 mb-4 flex items-center gap-2 text-xs label-mono text-muted-foreground"
          data-testid="autofill-status-banner"
        >
          <CheckCircle size={14} weight="bold" className="text-volt shrink-0" />
          <span>
            <span className="text-volt">1RM AUTO-FILL ACTIVE</span> — Main barbell lifts you add will be prefilled at{" "}
            {plan.level === "Beginner" ? "65%" : plan.level === "Advanced" ? "75%" : "70%"} of your logged 1RM
            (level: <span className="text-foreground">{plan.level}</span>).
          </span>
        </div>
      ) : (
        <div
          className="border border-border bg-card/50 px-3 py-2 mb-4 flex items-center gap-2 text-xs label-mono text-muted-foreground"
          data-testid="autofill-status-banner"
        >
          <span>
            NO 1RM ON FILE — log your assessment at{" "}
            <a href="/assessment" className="text-volt underline">/assessment</a>{" "}
            to auto-fill main-lift weights.
          </span>
        </div>
      )}

      <div className="space-y-8">
        {plan.weeks.map((w, wi) => (
          <WeekCard
            key={`week-${w.week_index}-${wi}`}
            wi={wi}
            week={w}
            completions={plan.completions}
            exMap={exMap}
            canRemove={plan.weeks.length > 1}
            onUpdateIntent={updateWeekIntent}
            onRemoveWeek={removeWeek}
            onUpdateDay={updateDay}
            onToggleComplete={toggleComplete}
            onReorder={reorderExercises}
            onChangeExercise={updateExercise}
            onRemoveExercise={removeExercise}
            onOpenPicker={(wi2, di2) => setPicker({ wi: wi2, di: di2 })}
            onProgressWeek={progressWeek}
            onShowExerciseInfo={onShowExerciseInfo}
            onReplaceExercises={replaceExercises}
            assessmentNotes={assessment?.notes || ""}
          />
        ))}
        <AddWeekMenu
          plan={plan}
          onAddEmpty={addWeek}
          onProgressWeek={progressWeek}
          isSaved={!!id}
        />
      </div>

      {picker && <ExercisePicker exercises={exercises} onPick={onPick} onClose={() => setPicker(null)} />}
      {info && (
        <ExerciseInfoModal
          exercise={exMap[info.exerciseId]}
          exMap={exMap}
          onClose={() => setInfo(null)}
          onSwap={onSwapAlternate}
        />
      )}
    </Layout>
  );
}
